import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCountries } from 'aurora-composition/modules/constant/actions';
import {
  resetFetchedDetails,
  fetchUAEResident,
  fetchNONUAEResident,
  fetchCompanyByLicNo,
  fetchGovernment,
  updateEntityCustomData,
} from 'aurora-composition/modules/common/actions';
import messages from 'aurora-translations';
import moment from 'moment';
import { keyEmptyCheck } from 'aurora-utils/utils';
import { PAGE_SIZE, getTypeFields } from './constants';

export default function useHelpers({ handleClose, addEntity, updateEntity, intl, serviceLabel, isSecondParty }) {
  const dispatch = useDispatch();

  const [{ locale }, { countries }, { fetchedEntity, entityCustomDetails }] = useSelector(state => [
    state.language,
    state.ConstantReducer,
    state.commonReducer,
  ]);
  const TYPE_FIELDS = useMemo(() => {
    return getTypeFields(isSecondParty);
  }, [isSecondParty]);
  const [formData, setFormData] = useState({});
  const [page, setPage] = useState(1);
  const [activeEntity, setActiveEntity] = useState(fetchedEntity[formData?.type]?.[0]);
  const entityDetails = fetchedEntity[formData?.type];

  useEffect(() => {
    setActiveEntity(fetchedEntity[formData?.type]?.[0]);
  }, [formData.type, fetchedEntity]);

  useEffect(() => {
    if (!countries?.length && formData?.type === 'NON_UAE_RESIDENT') dispatch(fetchCountries());
  }, [dispatch, formData?.type, countries]);

  function onCloseModal() {
    handleClose();
    setFormData({});
    dispatch(resetFetchedDetails());
  }

  const handleAdd = () => {
    addEntity({
      ...activeEntity,
      partyType: formData?.type,
      customEmail: entityCustomDetails?.customEmail || null,
      customPhoneNo: entityCustomDetails?.customPhoneNo || null,
    });
    handleClose();
    dispatch(resetFetchedDetails());
    // setFormData({});
  };
  const handleUpdate = () => {
    updateEntity({ ...formData });
  };

  function handleInputChange(name, value) {
    if (name === 'type') {
      setFormData({});
      dispatch(resetFetchedDetails());
      dispatch(updateEntityCustomData({ [name]: value }));
    }
    if (name === 'customEmail' || name === 'customPhoneNo') {
      dispatch(updateEntityCustomData({ [name]: value }));
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  function handleFetchDetails({ pageNum }) {
    switch (formData?.type) {
      case 'UAE_RESIDENT':
        dispatch(fetchUAEResident({ eidOrUnified: formData?.idn, params: { dateOfBirth: formData?.dob } }));
        break;
      case 'NON_UAE_RESIDENT':
        dispatch(
          fetchNONUAEResident({
            passport: formData?.Passport_number,
            nationalityId: formData?.nationality,
            email: formData?.email,
            phoneNumber: formData?.mobile,
          }),
        );
        break;
      case 'COMPANY':
        dispatch(fetchCompanyByLicNo(formData?.tradeLicenseNo));
        break;
      case 'GOVERNMENT_ENTITY':
        dispatch(
          fetchGovernment({
            page: pageNum ?? 0,
            size: PAGE_SIZE,
            licenseNumber: formData?.entity_id_number,
            nameAr: formData?.name_ar,
            nameEn: formData?.name_en,
          }),
        );
        break;
      default:
    }
  }

  function handlePageChange(pageNum) {
    setPage(pageNum);
    handleFetchDetails({ pageNum });
  }

  const NATIONALITIES = useMemo(() => {
    return countries?.map(({ nationalitynameAr, nationalityid, nationalitynameEn }) => ({
      label: locale === 'ar' ? nationalitynameAr : nationalitynameEn,
      value: nationalityid,
    }));
  }, [countries]);

  const SECTION_FIELDS = [
    {
      title: '',
      fields: [
        {
          label: messages[serviceLabel]?.id
            ? `${intl.formatMessage({ ...messages[serviceLabel] })} ${intl.formatMessage({ ...messages.type })}`
            : `${serviceLabel} ${intl.formatMessage({ ...messages.type })}`,
          placeholder: keyEmptyCheck('choose_option'),
          type: 'dropdown',
          required: true,
          name: 'type',
          onChange: (name, item) => handleInputChange(name, item?.value || ''),
          value: formData?.type,
          options: TYPE_FIELDS,
        },
        ...(formData?.type === 'UAE_RESIDENT'
          ? [
              {
                label: keyEmptyCheck('eid_moi_num'),
                placeholder: '00000',
                type: 'number',
                required: true,
                name: 'idn',
                value: formData?.idn,
                startAdornment: keyEmptyCheck('EID_MOI'),
                onChange: event => handleInputChange(event.target.name, event.target.value),
              },
              {
                name: 'dob',
                type: 'date',
                date: moment(formData?.dob).isValid() ? moment(formData?.dob).format('YYYY-MM-DD') : null,
                upperLimit: moment().format('YYYY-MM-DD'),
                onChange: date => handleInputChange('dob', moment(date).format('YYYY-MM-DD')),
                dateComponentProps: {
                  label: keyEmptyCheck('Date_of_birth'),
                  value: formData?.dob ? moment(formData?.dob).format('DD/MM/YYYY') : '',
                  autoComplete: 'off',
                  upperLimit: moment().format('YYYY-MM-DD'),
                  handleInputChange: date => date && handleInputChange('dob', moment(date).format('YYYY-MM-DD')),
                  required: true,
                  placeholder: intl.formatMessage({ ...messages.Date_of_birth_DD_MM_YYYY }),
                },
              },
            ]
          : []),
        ...(formData?.type === 'COMPANY'
          ? [
              {
                label: keyEmptyCheck('Trade_license_number'),
                type: 'text',
                required: true,
                name: 'tradeLicenseNo',
                value: formData?.tradeLicenseNo,
                onChange: event => handleInputChange(event.target.name, event.target.value),
              },
            ]
          : []),
        ...(formData?.type === 'NON_UAE_RESIDENT'
          ? [
              {
                label: keyEmptyCheck('nationality'),
                placeholder: keyEmptyCheck('nationality'),
                type: 'dropdown',
                required: true,
                name: 'nationality',
                onChange: (name, item) => handleInputChange(name, item?.value || ''),
                value: formData?.type,
                options: NATIONALITIES,
              },
              {
                label: keyEmptyCheck('Passport_number'),
                type: 'text',
                disabled: !formData?.nationality,
                placeholder: intl.formatMessage({ ...messages.Passport_number }),
                required: true,
                name: 'Passport_number',
                value: formData?.Passport_number,
                onChange: event => handleInputChange(event.target.name, event.target.value),
              },
              {
                label: keyEmptyCheck('email'),
                type: 'text',
                placeholder: intl.formatMessage({ ...messages.email }),
                name: 'email',
                value: formData?.email,
                onChange: event => handleInputChange(event.target.name, event.target.value),
              },
              {
                label: keyEmptyCheck('mobile'),
                type: 'text',
                placeholder: intl.formatMessage({ ...messages.mobile }),
                name: 'mobile',
                value: formData?.mobile,
                onChange: event => handleInputChange(event.target.name, event.target.value),
              },
            ]
          : []),
        ...(formData?.type === 'GOVERNMENT_ENTITY'
          ? [
              {
                label: keyEmptyCheck('entity_Id_number'),
                type: 'text',
                name: 'entity_id_number',
                value: formData?.entity_id_number,
                startAdornment: keyEmptyCheck('GOV'),
                onChange: event => handleInputChange(event.target.name, event.target.value),
              },
              {
                label: keyEmptyCheck('Name_Arabic_bracket'),
                type: 'text',
                placeholder: intl.formatMessage({ ...messages.Name_Arabic_hint }),
                name: 'name_ar',
                value: formData?.name_ar,
                onChange: event => handleInputChange(event.target.name, event.target.value),
              },
              {
                label: keyEmptyCheck('Name_English_bracket'),
                type: 'text',
                placeholder: intl.formatMessage({ ...messages.Name_English_hint }),
                name: 'name_en',
                value: formData?.name_en,
                onChange: event => handleInputChange(event.target.name, event.target.value),
              },
            ]
          : []),
      ],
    },
  ];
  const PHONE_MISSING =
    (entityCustomDetails?.type === 'UAE_RESIDENT' && !fetchedEntity[entityCustomDetails?.type]?.[0]?.phoneNumber) ||
    (entityCustomDetails?.type === 'NON_UAE_RESIDENT' && !fetchedEntity[entityCustomDetails?.type]?.[0]?.phoneNumber) ||
    (entityCustomDetails?.type === 'COMPANY' && !fetchedEntity[entityCustomDetails?.type]?.[0]?.mobileNumber);
  const EMAIL_MISSING =
    (entityCustomDetails?.type === 'UAE_RESIDENT' && !fetchedEntity[entityCustomDetails?.type]?.[0]?.email) ||
    (entityCustomDetails?.type === 'COMPANY' && !fetchedEntity[entityCustomDetails?.type]?.[0]?.companyEmail);
  const SECTION_EXTRA_FIELDS = [
    {
      title: '',
      fields: [
        ...(PHONE_MISSING
          ? [
              {
                label: keyEmptyCheck('Phone_Number'),
                placeholder: intl.formatMessage({ ...messages.enter_phone_number }),
                type: 'number',
                name: 'customPhoneNo',
                value: formData?.customPhoneNo,
                onChange: event => handleInputChange(event.target.name, event.target.value),
              },
            ]
          : []),
        ...(EMAIL_MISSING
          ? [
              {
                label: keyEmptyCheck('Email'),
                placeholder: intl.formatMessage({ ...messages.enter_email_address }),
                type: 'text',
                name: 'customEmail',
                value: formData?.customEmail,
                onChange: event => handleInputChange(event.target.name, event.target.value),
              },
            ]
          : []),
      ],
    },
  ];
  return {
    formData,
    page,
    activeEntity,
    setActiveEntity,
    entityDetails,
    handleAdd,
    handleUpdate,
    handleInputChange,
    handleFetchDetails,
    handlePageChange,
    setFormData,
    SECTION_FIELDS,
    SECTION_EXTRA_FIELDS,
    onCloseModal,
  };
}
